import BoundsSetter from 'components/AlertCreator/components/BoundsSetter';
import LocationSetter from 'components/AlertCreator/components/LocationSetter';
import ResetZoomMapEvent from 'components/ResetZoomMapEvent';
import ZoomOnFocusMapEvent from 'components/ZoomOnFocusMapEvent';
import DeviceFilters from 'features/integrations/features/ConnectedDevices/features/DeviceMap/components/DeviceFilters';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import AddCenterControlPositions from './components/AddCenterControlPositions';
import MapReset from './components/MapReset';
import MapView from './components/MapView';
import MapInvalidateSizeFix from './util/MapInvalidateSizeFix';

const MapSizeObserver = () => {
  const map = useMap();

  useEffect(() => {
    if (!map) return undefined;
    const container = map.getContainer?.();
    if (!container) return undefined;

    if (typeof ResizeObserver === 'undefined') {
      map.invalidateSize();
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      map.invalidateSize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);

  return null;
};

const BaseMap = ({
  mapCenter = [0, 0], // initial center of the map
  zoom = 5, // initial zoom level
  minZoom, // optional minimum zoom level
  maxZoom, // optional maximum zoom level
  bounds, // optional bounds to lock the user to a specific area
  scrollZoom = true, // enable or disable mouse scroll to zoom
  tileLayerUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', // picture of the map when not in satellite view (should be the url)
  enableFilters = false, // enable or disable device filters
  deviceTypeToFilter, // device type to filter on (if any)
  selectedGroup, // selected group to filter on (if any)
  setSelectedGroup, // function to set the selected group
  setMapBounds, // function to set the map bounds
  setUseThisDevice, // function to set the useThisDevice state
  setShowCurrentDevice, // function to set the showCurrentDevice state
  filtersCompact = false, // force compact filter UI
  disableLocationSync = false, // disable automatic center/zoom sync
  preserveLocationZoom = false, // preserve current zoom when syncing center (unless center provides zoom)
  mapRef = null, // optional ref to expose Leaflet map instance
  onMapReady = null, // optional callback with Leaflet map instance
  extraStack, // extra components to render in the MapView control stack in the bottom left
  children, // any additional children components to render on top of the map
}) => {
  const STREET_MAX_NATIVE_ZOOM = 19;
  const SATELLITE_MAX_NATIVE_ZOOM = 19;
  const resolvedTileMaxZoom = Number.isFinite(maxZoom) ? maxZoom : 23;

  // Track user interaction to toggle reset UI and preserve initial view.
  const [interacted, setInteracted] = useState(false);
  const [initPos, setInitPos] = useState(mapCenter);
  const [initZoom, setInitZoom] = useState(zoom);
  const [resetTrigger, setResetTrigger] = useState(false);
  // Satellite and labels are independent so users can mix layers.
  const [satelliteView, setSatelliteView] = useState(false);
  const [labelsEnabled, setLabelsEnabled] = useState(true);
  const internalMapRef = useRef(null);
  const resolvedMapRef = mapRef && typeof mapRef === 'object' ? mapRef : internalMapRef;

  useEffect(() => {
    // Only update if mapCenter actually changed (deep comparison for arrays)
    if (JSON.stringify(initPos) !== JSON.stringify(mapCenter)) {
      setInitPos(mapCenter);
    }
  }, [mapCenter, initPos]);

  useEffect(() => {
    // Only update if zoom actually changed
    if (initZoom !== zoom) {
      setInitZoom(zoom);
    }
  }, [zoom, initZoom]);

  const handleMapReady = useCallback(() => {
    if (typeof onMapReady !== 'function') return;
    const instance = resolvedMapRef?.current;
    if (instance) {
      onMapReady(instance);
    }
  }, [onMapReady, resolvedMapRef]);

  useEffect(() => {
    return () => {
      if (resolvedMapRef && typeof resolvedMapRef === 'object') {
        resolvedMapRef.current = null;
      }
    };
  }, [resolvedMapRef]);

  const handleResetClick = () => {
    setInteracted(false); // Reset interacted state
    setResetTrigger(new Date()); // Toggle to trigger useEffect in ResetZoomMapEvent
  };

  return (
    <MapContainer
      ref={resolvedMapRef}
      center={mapCenter}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      bounds={bounds}
      scrollWheelZoom={scrollZoom}
      style={{ width: '100%', height: '100%' }}
      whenReady={handleMapReady}
    >
      <MapSizeObserver />
      <MapInvalidateSizeFix />
      <AddCenterControlPositions />
      {/* Base tiles: use street map by default, satellite when toggled. */}
      <TileLayer
        url={
          !satelliteView
            ? tileLayerUrl
            : 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
        }
        maxZoom={resolvedTileMaxZoom}
        maxNativeZoom={satelliteView ? SATELLITE_MAX_NATIVE_ZOOM : STREET_MAX_NATIVE_ZOOM}
      />
      {/* Overlay labels on satellite tiles for place names. */}
      {labelsEnabled && satelliteView && (
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
          maxZoom={resolvedTileMaxZoom}
          maxNativeZoom={SATELLITE_MAX_NATIVE_ZOOM}
        />
      )}
      {/* Detect pan/zoom to reveal reset controls. */}
      <ZoomOnFocusMapEvent setInteracted={setInteracted} />
      {/* Reset map view to the initial center/zoom on demand. */}
      <ResetZoomMapEvent triggerReset={resetTrigger} initPos={initPos} initZoom={initZoom} />
      {enableFilters && (
        <DeviceFilters
          deviceTypeToFilter={deviceTypeToFilter}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          setMapBounds={setMapBounds}
          setUseThisDevice={setUseThisDevice}
          setShowCurrentDevice={setShowCurrentDevice}
          // Allow widgets to force compact filter UI regardless of viewport size.
          forceCompact={filtersCompact}
        />
      )}
      {/* Render UI controls for reset + layer toggles. */}
      <MapReset onClick={handleResetClick} interacted={interacted} />
      <MapView
        satelliteView={satelliteView}
        labelsEnabled={labelsEnabled}
        onToggleSatellite={() => setSatelliteView((prev) => !prev)}
        onToggleLabels={() => setLabelsEnabled((prev) => !prev)}
        extra={extraStack}
      />
      {!disableLocationSync && (
        <LocationSetter center={mapCenter} zoom={zoom} preserveZoom={preserveLocationZoom} />
      )}
      <BoundsSetter bounds={bounds} />
      {children}
    </MapContainer>
  );
};

export default BaseMap;
